import React, { useEffect, useState } from 'react';
import { Comment } from '@mui/icons-material';
import { Box, Button, Dialog, DialogActions, Divider, FormControl, TextareaAutosize, capitalize } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { CRUDMode } from '/src/lib/types';

export type NoteModal = {
  /**
   * CRUD operation to perform
   */
  mode: CRUDMode;
  /**
   * Whether this component is visible
   */
  open: boolean;
  /**
   * Modal content
   */
  content?: string;
  /**
   * Sets component visibility
   * @param open Whether the component is visible
   * @returns void
   */
  setOpen: (open: boolean) => void;
  /**
   * Called when `Confirm` button is pressed
   * @param content Updated content (optional)
   * @returns void
   */
  onConfirm: (content?: string) => void;
  /**
   * Called when the `Cancel` button is pressed
   * @returns void
   */
  onCancel?: () => void;
};

export const NoteModal: React.FC<NoteModal> = observer(({ mode, open, content, setOpen, onConfirm, onCancel }) => {
  const [noteContent, setNoteContent] = useState('');

  const action = capitalize(mode);

  const handleConfirm = () => {
    setOpen(false);
    onConfirm(noteContent);
    setNoteContent('');
  };

  const handleCancel = () => {
    setOpen(false);
    setNoteContent('');
    onCancel?.();
  };

  useEffect(() => {
    if (content && content !== noteContent) setNoteContent(content);
  }, [content]);

  return (
    <Dialog open={open} aria-label={`${action} Note`}>
      <Box className="toolbar-div" width="500px">
        <Box display="flex" justifyContent="center" alignItems="center" padding="none" className="md-title" gap="6px">
          <Comment /> {action} Note
        </Box>
      </Box>
      <Box>
        <FormControl variant="standard" fullWidth margin="dense">
          <TextareaAutosize
            value={noteContent}
            onChange={(event) => setNoteContent(event.target.value)}
            minRows={10}
            maxRows={10}
            style={{ width: '100%', border: 'none', resize: 'none', padding: '0 8px', outline: 'none' }}
            id="noteContent"
            aria-label="Enter the Note's text"
            required
          />
        </FormControl>
      </Box>
      <Divider />
      <DialogActions>
        <Button onClick={() => handleCancel()} className="md-primary">
          Cancel
        </Button>
        <Button onClick={() => handleConfirm()} className="md-primary">
          {mode == CRUDMode.Create && 'Add'}
          {mode == CRUDMode.Edit && 'Save'}
          {mode == CRUDMode.Remove && 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
});
